import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { ComponentProps, MouseEvent, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, SearchFacet, SearchFacetItem } from '@/pages/Search/SearchPage/types';
import { useAppSelector } from '@/redux/hooks';
import { getAggregationsOnDemandStatus } from '@/redux/modules/search/search.selectors';
import { getSearchFacets, getSelectedCategoryFacetValue } from '@/redux/modules/searchFacetAggregates.selectors';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { getCategoryBrowseNavigateUrl } from '@/utils/browsePath/buildCategoryBrowseNavigateUrl';
import {
    addValueToParamsList,
    modifyParamsRemovePage,
    modifyParamsWithTaxonomyAll,
    modifyParamsWithTaxonomySelected,
    parseQueryParams,
} from '@/utils/queryParams';
import { getSearchUrlWithParams, isCategoryPage, isPriceGuidePage } from '@/utils/urls';

import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';
import { CategoryBackLinks } from './components/CategoryBackLinks/CategoryBackLinks';

type Props = {
    currentOpenMenu?: CurrentOpenMenu;
    isAllFilters?: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
};

export const CategoryFilter = ({ currentOpenMenu, isAllFilters, onMenuClose, onMenuOpen }: Props) => {
    const { formatMessage } = useIntl();
    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const isOnBrowseCategoryPage = isCategoryPage(pathname) || isPriceGuidePage(pathname);
    const currentCategoryId = queryParams[paramTypes.CATEGORY_ID]?.toString();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();

    const handleMenuOpen: ComponentProps<typeof ConditionalMenuContainer>['onMenuOpen'] = useCallback(
        (menu) => {
            if (!isAllFilters) {
                trackQuickFilterPillClicked(QuickFilterName.Category);
            }
            onMenuOpen(menu);
        },
        [isAllFilters, onMenuOpen, trackQuickFilterPillClicked]
    );

    const facets = useAppSelector(getSearchFacets);
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);

    const nestedCategoryFacet = useAppSelector((state) => getSelectedCategoryFacetValue(state, FacetTypes.CATEGORY_ID));

    const navigateWithParams = useNavigateSearchPageWithParams();

    const handleNavigation = useCallback(
        (event: MouseEvent<HTMLAnchorElement | HTMLInputElement>, params: any) => {
            event.preventDefault();
            const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Category;

            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: keepMenuOpenValue,
                filterAction: params.category_id ? FilterAction.CategoryChanged : FilterAction.CategoryRemoved,
                params,
                preventScrollReset: true,
            });
        },
        [isAllFilters, navigateWithParams]
    );

    const buildCategoryHref = (categoryId: string, remove?: boolean) => {
        let newParams = modifyParamsRemovePage(queryParams);
        if (remove) {
            delete newParams[paramTypes.CATEGORY_ID];
        } else if (currentCategoryId?.includes(',')) {
            newParams = modifyParamsWithTaxonomySelected(newParams, FacetTypes.CATEGORY_ID, categoryId, facets);
        } else {
            delete newParams[paramTypes.CATEGORY_ID];
            newParams = addValueToParamsList(newParams, paramTypes.CATEGORY_ID, categoryId);
        }

        return newParams;
    };

    const buildCategoryNavigateUrl = useCallback(
        (params: ReturnType<typeof buildCategoryHref>) =>
            isOnBrowseCategoryPage
                ? getCategoryBrowseNavigateUrl(pathname, params, facets)
                : getSearchUrlWithParams(params),
        [facets, isOnBrowseCategoryPage, pathname]
    );

    const onClearCategoryFromSearch = useCallback(
        (event: MouseEvent<HTMLInputElement>) => {
            let newParams = modifyParamsRemovePage(queryParams);

            delete newParams[paramTypes.CATEGORY_ID];
            newParams = modifyParamsWithTaxonomyAll(newParams, FacetTypes.CATEGORY_ID, currentCategoryId);
            handleNavigation(event, newParams);
        },
        [currentCategoryId, handleNavigation, queryParams]
    );

    const categoryFacet = useMemo(() => {
        return facets.find((facet) => facet.id === FacetTypes.CATEGORY_ID) as SearchFacet | undefined;
    }, [facets]);

    const categoryLabel = formatMessage({ id: `search_facet_title_${FacetTypes.CATEGORY_ID}` });

    if (!categoryFacet) {
        if (aggregationsOnDemandStatus !== 'error') {
            // Search without facets yet (skipAggs was used): show a placeholder chip so users
            // can click to trigger on-demand facet loading via onMenuOpen → handleMenuOpen
            return (
                <ConditionalMenuContainer
                    currentOpenMenu={currentOpenMenu}
                    currentOpenMenuId={CurrentOpenMenu.Category}
                    isAllFilters={isAllFilters}
                    onMenuClose={onMenuClose}
                    onMenuOpen={handleMenuOpen}
                    title={categoryLabel}
                    trigger={<Chip style="singleFilter">{categoryLabel}</Chip>}
                >
                    <Loading />
                </ConditionalMenuContainer>
            );
        }
        return null;
    }
    const { categories, id } = categoryFacet;

    // Look for current category in both top-level and nested categories
    const currentCategory: SearchFacetItem | undefined = currentCategoryId
        ? categoryFacet?.categories?.facets?.find((category) => category.id === currentCategoryId) ||
          nestedCategoryFacet?.categories?.find?.((category) => category.id === currentCategoryId) ||
          // If nestedCategoryFacet itself matches the ID, use it directly
          (nestedCategoryFacet?.id === currentCategoryId ? nestedCategoryFacet : undefined)
        : undefined;

    return (
        <ConditionalMenuContainer
            accordionDefaultOpen
            currentOpenMenu={currentOpenMenu}
            currentOpenMenuId={CurrentOpenMenu.Category}
            isAllFilters={isAllFilters}
            onMenuClose={onMenuClose}
            onMenuOpen={handleMenuOpen}
            title={formatMessage({ id: `search_facet_title_${id}` })}
            trigger={
                <Chip
                    onClose={currentCategoryId ? onClearCategoryFromSearch : undefined}
                    selected={Boolean(currentCategoryId)}
                    style="singleFilter"
                >
                    {currentCategory?.name || categoryLabel}
                </Chip>
            }
        >
            <CategoryBackLinks
                currentOpenMenu={currentOpenMenu}
                isAllFilters={isAllFilters}
                facets={facets}
            />

            {/* L1 Categories */}
            {Boolean(!nestedCategoryFacet.categories) &&
                categories?.facets[0]?.id !== currentCategoryId &&
                categories.facets.map((option) => (
                    <SubtleMonochromeLink
                        href={buildCategoryNavigateUrl(buildCategoryHref(option.id))}
                        key={option.id}
                        onClick={(event) => handleNavigation(event, buildCategoryHref(option.id))}
                    >
                        {option.name}
                    </SubtleMonochromeLink>
                ))}
            {/* ALL OTHER LEVELS */}
            {Boolean(nestedCategoryFacet.categories) && (
                <>
                    <H5 className="text-link-primary">{nestedCategoryFacet.name}</H5>
                    {nestedCategoryFacet.categories.map((option) => (
                        <SubtleMonochromeLink
                            href={buildCategoryNavigateUrl(buildCategoryHref(option.id))}
                            key={option.id}
                            onClick={(event) => handleNavigation(event, buildCategoryHref(option.id))}
                        >
                            {option.name}
                        </SubtleMonochromeLink>
                    ))}
                </>
            )}
        </ConditionalMenuContainer>
    );
};
